import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  FlatList,
  TouchableOpacity,
  Image,
  Modal,
  TextInput,
  Alert,
  RefreshControl,
} from 'react-native';
import { useAuth } from '../../Utils/AuthProvider';
import { DeviceEventEmitter } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import PostCard from '../../Components/PostCard';
import { theme } from '../../constants/theme';
import { getUserData } from '../../services/userService';
import { fetchAnnouncementCommunities } from '../../services/communityService';
import { fetchCommunityAnnouncements, fetchAllPublicAnnouncements } from '../../services/postService';
import { canPostAnnouncement } from '../../services/communityService';
import { markAllAnnouncementsAsRead } from '../../services/announcementService';
import { Supabase } from '../../Utils/supabase.config';
import AnnouncementPost from '../../Components/AnnouncementPost';
import ScreenWrapper from '../../Components/ScreenWrapper';
import Header from '../../Components/Header';
import defaultCommunityImg from '../../../assets/images/defaultuser.png';

const tabs = [
  { key: 'feed', label: 'Announcements' },
];

const AboutTab = () => {
  const { user } = useAuth();
  const [announcementCommunities, setAnnouncementCommunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnnouncementCommunities();
  }, []);

  const loadAnnouncementCommunities = async () => {
    setLoading(true);
    const res = await fetchAnnouncementCommunities();
    if (res.success && res.data) {
      setAnnouncementCommunities(res.data);
    } else {
      setError('Failed to load announcement communities');
    }
    setLoading(false);
  };

  if (loading) return <ActivityIndicator style={{ marginTop: 32 }} />;
  if (error) return <Text style={styles.error}>{error}</Text>;

  return (
    <ScrollView contentContainerStyle={{ padding: 24 }}>
      <Text style={styles.aboutTitle}>📢 Announcements</Text>
      <Text style={styles.aboutDesc}>
        Welcome to the Announcements section! Here you can find important updates, 
        news, and official communications from our community administrators.
      </Text>
      
      <View style={styles.infoBox}>
        <Text style={styles.infoTitle}>ℹ️ How it works</Text>
        <Text style={styles.infoText}>
          • Only admins and moderators can post announcements{'\n'}
          • All users can create communities, posts, comments, and likes{'\n'}
          • Announcements appear in the Feed tab{'\n'}
          • Important announcements are pinned to the top{'\n'}
          • All members can view and interact with announcements
        </Text>
      </View>

      {announcementCommunities.length > 0 && (
        <View style={styles.communitiesSection}>
          <Text style={styles.sectionTitle}>Announcement Communities</Text>
          {announcementCommunities.map((community: any) => (
            <View key={community.id} style={styles.communityItem}>
              <Image
                source={community.profile_image ? { uri: community.profile_image } : defaultCommunityImg}
                style={styles.communityAvatar}
              />
              <View style={styles.communityInfo}>
                <Text style={styles.communityName}>{community.name}</Text>
                <Text style={styles.communityDesc}>{community.description}</Text>
              </View>
            </View>
          ))}
        </View>
      )}

      <View style={styles.guidelinesBox}>
        <Text style={styles.guidelinesTitle}>📋 Guidelines</Text>
        <Text style={styles.guidelinesText}>
          • Keep announcements clear and concise{'\n'}
          • Use for important updates and information{'\n'}
          • Respect community guidelines{'\n'}
          • Contact admins if you have important information to share{'\n'}
          • Regular posts can be created in any community
        </Text>
      </View>
    </ScrollView>
  );
};

const FeedTab = () => {
  const { user } = useAuth();
  const PostCardComponent: any = PostCard;
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [canPost, setCanPost] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [annViewableSet, setAnnViewableSet] = useState<Set<number | string>>(new Set());
  const onAnnViewableItemsChanged = React.useRef((_: any) => {}).current; // disabled auto-pause integration
  const annViewConfigRef = React.useRef({ viewAreaCoveragePercentThreshold: 50 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [authorAdminMap, setAuthorAdminMap] = useState<Record<string, boolean>>({});
  const [communityLinkAllowedMap, setCommunityLinkAllowedMap] = useState<Record<string, boolean>>({});

  // Use useFocusEffect to refresh announcements when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadAllAnnouncements();
      checkPermissions();
      markAnnouncementsAsRead();

      return () => {
        // Pause all videos when leaving Announcements screen
        try { DeviceEventEmitter.emit('pauseAllVideos'); } catch (_) {}
      };
    }, [])
  );

  const markAnnouncementsAsRead = async () => {
    if (!user?.id) return;
    
    try {
      await markAllAnnouncementsAsRead(user.id);
    } catch (error) {
      console.error('Error marking announcements as read:', error);
    }
  };

  // Real-time subscriptions for likes and comments
  useEffect(() => {
    if (!user) return;

    // Subscribe to post likes changes
    const likesChannel = Supabase
      .channel('announcement-likes')
      .on('postgres_changes' as any, { 
        event: '*', 
        schema: 'public', 
        table: 'postLikes',
        filter: `postId=in.(${announcements.map(a => a.id).join(',')})`
      } as any, (payload: any) => {
        if (payload.eventType === 'INSERT') {
          setAnnouncements(prev => prev.map(post => {
            if (post.id === payload.new.postId) {
              return {
                ...post,
                postLikes: [...(post.postLikes || []), payload.new]
              };
            }
            return post;
          }));
        } else if (payload.eventType === 'DELETE') {
          setAnnouncements(prev => prev.map(post => {
            if (post.id === payload.old.postId) {
              return {
                ...post,
                postLikes: (post.postLikes || []).filter((like: any) => 
                  !(like.userId === payload.old.userId && like.postId === payload.old.postId)
                )
              };
            }
            return post;
          }));
        }
      })
      .subscribe();

    // Subscribe to comments changes
    const commentsChannel = Supabase
      .channel('announcement-comments')
      .on('postgres_changes' as any, { 
        event: '*', 
        schema: 'public', 
        table: 'comments',
        filter: `postId=in.(${announcements.map(a => a.id).join(',')})`
      } as any, (payload: any) => {
        if (payload.eventType === 'INSERT') {
          setAnnouncements(prev => prev.map(post => {
            if (post.id === payload.new.postId) {
              const currentCount = post.comments?.[0]?.count || 0;
              return {
                ...post,
                comments: [{ count: currentCount + 1 }]
              };
            }
            return post;
          }));
        } else if (payload.eventType === 'DELETE') {
          setAnnouncements(prev => prev.map(post => {
            if (post.id === payload.old.postId) {
              const currentCount = post.comments?.[0]?.count || 0;
              return {
                ...post,
                comments: [{ count: Math.max(0, currentCount - 1) }]
              };
            }
            return post;
          }));
        }
      })
      .subscribe();

    return () => {
      Supabase.removeChannel(likesChannel);
      Supabase.removeChannel(commentsChannel);
    };
  }, [user, announcements.map(a => a.id).join(',')]);

  const loadAllAnnouncements = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const offset = isLoadMore ? currentPage * pageSize : 0;
      const limit = pageSize;
      
      // Fetch announcements with pagination
      const announcementsRes = await fetchAllPublicAnnouncements(limit, offset);
      if (announcementsRes.success && announcementsRes.data) {
        // Normalize comment structure to ensure consistency
        const normalizedData = announcementsRes.data.map((post: any) => ({
          ...post,
          comments: post.comments && Array.isArray(post.comments) && post.comments.length > 0 
            ? post.comments 
            : [{count: 0}]
        }));

        // Update community link-allowed map from this batch
        setCommunityLinkAllowedMap(prev => {
          const next = { ...prev };
          normalizedData.forEach((post: any) => {
            const communityId = String(post?.community_id || post?.communityId || '');
            const isAllowed = post?.community && typeof (post.community as any).is_linkallowed === 'boolean'
              ? Boolean((post.community as any).is_linkallowed)
              : undefined;
            if (communityId && typeof isAllowed === 'boolean' && next[communityId] === undefined) {
              next[communityId] = isAllowed;
            }
          });
          return next;
        });

        // Batch-check admin status for (author, community) pairs
        try {
          const pairs: { key: string; userId: string; communityId: string }[] = [];
          const seen = new Set<string>();

          normalizedData.forEach((post: any) => {
            const userId = String(post?.userId || post?.user_id || post?.user?.id || '');
            const communityId = String(post?.community_id || post?.communityId || '');
            if (!userId || !communityId) return;
            const key = `${userId}|${communityId}`;
            if (authorAdminMap[key] !== undefined || seen.has(key)) return;
            seen.add(key);
            pairs.push({ key, userId, communityId });
          });

          if (pairs.length > 0) {
            const results = await Promise.all(
              pairs.map(async ({ key, userId, communityId }) => {
                try {
                  const { data, error } = await Supabase.rpc('check_user_community_admin', {
                    user_uuid: userId,
                    community_uuid: communityId,
                  });
                  if (error) {
                    return { key, isAdmin: false };
                  }
                  return { key, isAdmin: Boolean(data) };
                } catch {
                  return { key, isAdmin: false };
                }
              })
            );

            setAuthorAdminMap(prev => {
              const next = { ...prev };
              results.forEach(r => {
                next[r.key] = r.isAdmin;
              });
              return next;
            });
          }
        } catch {
          // Safe fallback: links remain plain text
        }
        
        // Ensure we don't exceed the limit
        const safeData = normalizedData.slice(0, limit);
        
        if (isLoadMore) {
          // Check for duplicates before appending
          setAnnouncements(prev => {
            const existingIds = new Set(prev.map(post => post.id));
            const newPosts = safeData.filter(post => !existingIds.has(post.id));
            return [...prev, ...newPosts];
          });
          setCurrentPage(prev => prev + 1);
        } else {
          setAnnouncements(safeData);
          setCurrentPage(1);
        }
        
        // Check if we have more data - only if we got the full limit
        setHasMore(safeData.length >= limit);
      } else {
        setError('Failed to load announcements');
        setHasMore(false);
      }
    } catch (error) {
      setError('Failed to load announcements');
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setCurrentPage(0);
    setHasMore(true);
    await loadAllAnnouncements();
    await checkPermissions();
    setRefreshing(false);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && !refreshing) {
      loadAllAnnouncements(true);
    }
  };

  const checkPermissions = async () => {
    if (!user) return;
    
    const communitiesRes = await fetchAnnouncementCommunities();
    
    if (!communitiesRes.success || !communitiesRes.data || communitiesRes.data.length === 0) {
      return;
    }
    
    for (const community of communitiesRes.data) {
      const canPostRes = await canPostAnnouncement(user.id, community.id);
      if (canPostRes.success && canPostRes.data) {
        setCanPost(true);
        setSelectedCommunity(community);
        return;
      }
    }
  };

  const handleEditAnnouncement = (announcement: any) => {
    setEditingAnnouncement(announcement);
    setIsEditMode(true);
    setShowAnnouncementModal(true);
  };

  const handleCloseModal = () => {
    setShowAnnouncementModal(false);
    setIsEditMode(false);
    setEditingAnnouncement(null);
  };

  const handleNewAnnouncement = () => {
    setIsEditMode(false);
    setEditingAnnouncement(null);
    setShowAnnouncementModal(true);
  };

  if (loading) return <ActivityIndicator style={{ marginTop: 32 }} />;
  if (error) return <Text style={styles.error}>{error}</Text>;

  return (
    <View style={{ flex: 1 }}>
      {canPost && selectedCommunity && (
        <TouchableOpacity
          style={styles.newAnnouncementButton}
          onPress={handleNewAnnouncement}
        >
          <Text style={styles.newAnnouncementButtonText}>📢 Post Announcement</Text>
        </TouchableOpacity>
      )}

      {announcements.length > 0 ? (
        <FlatList
          data={announcements}
          renderItem={({ item }) => {
            const communityId = String((item as any)?.community_id || (item as any)?.communityId || '');
            const userId = String((item as any)?.userId || (item as any)?.user_id || (item as any)?.user?.id || '');
            const adminKey = `${userId}|${communityId}`;
            const allowLinks =
              Boolean(communityId && communityLinkAllowedMap[communityId]) &&
              Boolean(userId && authorAdminMap[adminKey]);

            return (
              <PostCardComponent
                item={item}
                currentUser={user}
                showDelete={canPost}
                onDelete={(post: any) => setAnnouncements(prev => prev.filter(p => p.id !== post.id))}
                onEdit={canPost ? handleEditAnnouncement : undefined}
                allowLinks={allowLinks}
              />
            );
          }}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={{ padding: 16 }}
          // REMOVED: onEndReached - causes automatic loading of all announcements
          // Replaced with "Load More" button for better performance
          removeClippedSubviews
          windowSize={5}
          initialNumToRender={5}
          maxToRenderPerBatch={5}
          updateCellsBatchingPeriod={100}
          ListFooterComponent={
            hasMore ? (
              <View style={styles.loadMoreContainer}>
                <TouchableOpacity 
                  onPress={handleLoadMore}
                  style={[styles.loadMoreButton, (loadingMore || refreshing) && styles.loadMoreButtonDisabled]}
                  disabled={loadingMore || refreshing}
                  activeOpacity={0.7}
                >
                  <Text style={styles.loadMoreText}>
                    {loadingMore ? 'Loading...' : 'Load More Announcements'}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : announcements.length > 0 ? (
              <View style={{ marginVertical: 30 }}>
                <Text style={styles.empty}>No more announcements</Text>
              </View>
            ) : null
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      ) : (
        <Text style={styles.empty}>No announcements yet</Text>
      )}

      {selectedCommunity && (
        <AnnouncementPost
          communityId={selectedCommunity.id}
          visible={showAnnouncementModal}
          onClose={handleCloseModal}
          onSuccess={loadAllAnnouncements}
          editPost={editingAnnouncement}
          isEditMode={isEditMode}
        />
      )}
    </View>
  );
};

const Announcements = () => {
  const [selected, setSelected] = useState('feed');

  return (
    <ScreenWrapper bg="white">
      <Header title="Announcements" />
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        {selected === 'feed' && <FeedTab />}
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#f2f2f2',
    padding: 8,
    borderRadius: 8,
    margin: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    marginHorizontal: 4,
    borderRadius: 8,
    backgroundColor: '#eee',
  },
  tabSelected: {
    backgroundColor: theme.colors.primary,
  },
  tabLabel: {
    color: '#222',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  tabLabelSelected: {
    color: '#fff',
  },
  aboutTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#222',
  },
  aboutDesc: {
    fontSize: 16,
    color: '#555',
    marginBottom: 24,
    lineHeight: 24,
  },
  infoBox: {
    backgroundColor: theme.colors.gray,
    padding: 20,
    borderRadius: 12,
    marginBottom: 24,
  },
  infoTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 12,
  },
  infoText: {
    fontSize: 14,
    color: theme.colors.textLight,
    lineHeight: 20,
  },
  communitiesSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 16,
  },
  communityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f8f8f8',
    borderRadius: 12,
    marginBottom: 12,
  },
  communityAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 16,
  },
  communityInfo: {
    flex: 1,
  },
  communityName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.dark,
    marginBottom: 4,
  },
  communityDesc: {
    fontSize: 14,
    color: theme.colors.textLight,
  },
  guidelinesBox: {
    backgroundColor: theme.colors.primary,
    padding: 20,
    borderRadius: 12,
  },
  guidelinesTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
    marginBottom: 12,
  },
  guidelinesText: {
    fontSize: 14,
    color: 'white',
    lineHeight: 20,
    opacity: 0.9,
  },
  newAnnouncementButton: {
    backgroundColor: theme.colors.primary,
    padding: 12,
    borderRadius: 8,
    margin: 16,
    alignItems: 'center',
  },
  newAnnouncementButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  error: {
    color: 'red',
    textAlign: 'center',
    marginTop: 32,
  },
  empty: {
    color: '#888',
    textAlign: 'center',
    marginTop: 32,
  },
  loadMoreContainer: {
    marginVertical: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  loadMoreButtonDisabled: {
    opacity: 0.6,
  },
});

export default Announcements; 